"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Slices = void 0;
const tslib_1 = require("tslib");
const AvlMap_1 = require("sonic-forest/lib/avl/AvlMap");
const printTree_1 = require("tree-dump/lib/printTree");
const Slice_1 = require("./Slice");
const clock_1 = require("../../../json-crdt-patch/clock");
const hash_1 = require("../../../json-crdt/hash");
const hash_2 = require("../../../json-hash/hash");
const constants_1 = require("./constants");
const nodes_1 = require("../../../json-crdt/nodes");
const constants_2 = require("../rga/constants");
const iterator_1 = require("../../../util/iterator");
const schema = tslib_1.__importStar(require("./schema"));
class Slices {
    constructor(
    /** The text RGA. */
    txt, 
    /** The `arr` node, used as a set, where slices are stored. */
    set) {
        this.txt = txt;
        this.set = set;
        this.list = new AvlMap_1.AvlMap(clock_1.compare);
        // ----------------------------------------------------------------- Stateful
        this._topologyHash = 0;
        this.hash = 0;
        this.rga = txt.str;
    }
    ins(range, stacking, type, data, Klass = Slice_1.Slice, padded = false) {
        const slicesModel = this.set.doc;
        const arr = this.set;
        const api = slicesModel.api;
        const txt = this.txt;
        const rga = txt.str;
        const builder = api.builder;
        const stepId = builder.vec();
        const start = range.start.clone();
        const end = range.end.clone();
        if (padded) {
            start.refAfter();
            end.refAfter();
            const afterIdForLeadPad = start.id;
            const afterIdForTrailPad = end.id;
            builder.nop(1);
            const leadPadId = builder.insStr(rga.id, afterIdForLeadPad, ' ');
            builder.nop(1);
            const trailPadId = builder.insStr(rga.id, afterIdForTrailPad, ' ');
            start.id = leadPadId;
            start.anchor = constants_2.Anchor.Before;
            end.id = trailPadId;
            end.anchor = constants_2.Anchor.After;
        }
        stacking = stacking & 0b111;
        const header = (stacking << constants_1.SliceHeaderShift.Stacking) +
            ((start.anchor & 0b1) << constants_1.SliceHeaderShift.X1Anchor) +
            ((end.anchor & 0b1) << constants_1.SliceHeaderShift.X2Anchor);
        const headerId = builder.con(header);
        const x1Id = builder.con(start.id);
        const x2Id = builder.con((0, clock_1.compare)(start.id, end.id) === 0 ? 0 : end.id);
        const typeId = schema.type(type).build(builder);
        const tupleKeysUpdate = [
            [constants_1.SliceTupleIndex.Header, headerId],
            [constants_1.SliceTupleIndex.X1, x1Id],
            // TODO: Make `x2Id` undefined, when `start.id` and `end.id` are equal.
            [constants_1.SliceTupleIndex.X2, x2Id],
            [constants_1.SliceTupleIndex.Type, typeId],
        ];
        if (data !== undefined)
            tupleKeysUpdate.push([constants_1.SliceTupleIndex.Data, builder.json(data)]);
        builder.insVec(stepId, tupleKeysUpdate);
        const chunkId = builder.insArr(arr.id, arr.id, [stepId]);
        if (padded) {
            builder.del(rga.id, [(0, clock_1.tss)(start.id.sid, start.id.time, 1)]);
            builder.del(rga.id, [(0, clock_1.tss)(end.id.sid, end.id.time, 1)]);
        }
        api.apply();
        const tuple = slicesModel.index.get(stepId);
        const chunk = arr.findById(chunkId);
        // TODO: Need to check if split slice text was deleted
        const slice = new Klass(slicesModel, this.txt, arr, chunk, tuple, stacking, start, end);
        this.list.set(chunk.id, slice);
        return slice;
    }
    insMarker(range, type, data) {
        return this.ins(range, constants_1.SliceStacking.Marker, type, data);
    }
    insMarkerAfter(after, type, data) {
        // TODO: test condition when cursors is at absolute or relative starts
        const txt = this.txt;
        const api = txt.model.api;
        const builder = api.builder;
        /**
         * We skip one clock cycle to prevent Block-wise RGA from merging adjacent
         * characters. We want the marker chunk to always be its own distinct chunk.
         */
        builder.nop(1);
        // TODO: Handle case when marker is inserted at the abs start, prevent abs start/end inserts.
        const textId = builder.insStr(txt.str.id, after, '\n');
        api.apply();
        const point = txt.point(textId, constants_2.Anchor.Before);
        const range = txt.range(point, point.clone());
        return this.insMarker(range, type, data);
    }
    insStack(range, type, data, padded) {
        return this.ins(range, constants_1.SliceStacking.Many, type, data, void 0, padded);
    }
    insOne(range, type, data, padded) {
        return this.ins(range, constants_1.SliceStacking.One, type, data, void 0, padded);
    }
    insErase(range, type, data) {
        return this.ins(range, constants_1.SliceStacking.Erase, type, data);
    }
    insAtomic(range, type, data, padded) {
        return this.ins(range, constants_1.SliceStacking.Atomic, type, data, void 0, padded);
    }
    unpack(arr, chunk) {
        const txt = this.txt;
        const model = this.set.doc;
        const tupleId = chunk.data ? chunk.data[0] : undefined;
        if (!tupleId)
            throw new Error('SLICE_NOT_FOUND');
        const tuple = model.index.get(tupleId);
        if (!(tuple instanceof nodes_1.VecNode))
            throw new Error('NOT_TUPLE');
        let slice = Slice_1.Slice.deserialize(model, txt, arr, chunk, tuple);
        if (slice.isMarker())
            slice = new Slice_1.Slice(model, txt, arr, chunk, tuple, slice.stacking, slice.start, slice.end);
        return slice;
    }
    get(id) {
        return this.list.get(id);
    }
    del(id) {
        this.list.del(id);
        const set = this.set;
        const api = set.doc.api;
        if (!set.findById(id))
            return;
        // TODO: Is it worth checking if the slice is already deleted?
        api.builder.del(set.id, [(0, clock_1.tss)(id.sid, id.time, 1)]);
        api.apply();
    }
    delSlices(slices) {
        const set = this.set;
        const doc = set.doc;
        const api = doc.api;
        const spans = [];
        for (const slice of slices) {
            if (slice instanceof Slice_1.Slice) {
                const id = slice.id;
                if (!set.findById(id))
                    continue;
                spans.push(new clock_1.Timespan(id.sid, id.time, 1));
            }
        }
        if (!spans.length)
            return false;
        api.builder.del(this.set.id, spans);
        api.apply();
        return true;
    }
    size() {
        return this.list._size;
    }
    /**
     * @todo Rename to `each0`.
     */
    iterator0() {
        const iterator = this.list.iterator0();
        return () => iterator()?.v;
    }
    each() {
        return new iterator_1.UndEndIterator(this.iterator0());
    }
    forEach(callback) {
        this.list.forEach((node) => callback(node.v));
    }
    refresh() {
        const topologyHash = (0, hash_1.updateRga)(hash_2.CONST.START_STATE, this.set);
        if (topologyHash !== this._topologyHash) {
            this._topologyHash = topologyHash;
            let chunk;
            for (const iterator = this.set.iterator(); (chunk = iterator());) {
                const item = this.list.get(chunk.id);
                if (chunk.del) {
                    if (item)
                        this.list.del(chunk.id);
                }
                else {
                    if (!item) {
                        try {
                            this.list.set(chunk.id, this.unpack(this.set, chunk));
                        }
                        catch (error) {
                            if (process.env.NODE_ENV !== 'production') {
                                // A single corrupt slice should not break the whole document.
                                // Skip the bad chunk so refresh() can continue with the rest.
                                // The chunk stays absent from `list`; it will be retried on the
                                // next topology change in case the model heals.
                                // eslint-disable-next-line no-console
                                console.warn('Slices.refresh: failed to unpack slice chunk', error);
                            }
                        }
                    }
                }
            }
        }
        let hash = topologyHash;
        this.list.forEach(({ v: item }) => {
            try {
                item.refresh();
                hash = (0, hash_2.updateNum)(hash, item.hash);
            }
            catch (error) {
                if (process.env.NODE_ENV !== 'production') {
                    // eslint-disable-next-line no-console
                    console.warn('Slices.refresh: failed to refresh slice', error);
                }
            }
        });
        return (this.hash = hash);
    }
    // ---------------------------------------------------------------- Printable
    toStringName() {
        return 'Slices';
    }
    toString(tab = '') {
        return (this.toStringName() +
            (0, printTree_1.printTree)(tab, [...this.list.entries()].map(({ v }) => (tab) => v.toString(tab))));
    }
}
exports.Slices = Slices;
